import React, { Component } from "react";
import { Spin } from "antd";
import Swal from "sweetalert2";
import "../ORDER/OrderMaster/style.css";
import "antd/dist/antd.css";

const API_BASE = "https://api.blumangoes.ai:6143";
const DB = "IMAGEDB";
const CONCURRENCY = "300";
const POLL_INTERVAL_MS = 3000;

const PORTALS = [
  "MYNTRA",
  "FLIPKART",
  "AMAZON-IN & UAE",
  "AJIO",
  "SHOPIFY",
  "NAMSHI",
  "DUBAI MALL",
];

const PORTAL_CONDITIONS = {
  "MYNTRA":           ["Size (500KB-1MB)", "Dimensions (>=1080x1440)", "Resolution (>=72 DPI)", "Aspect Ratio (3:4)", "No Dropbox/GDrive Link"],
  "FLIPKART":         ["Dimensions (>=200x800)", "Format (JPEG only)", "Color Mode (RGB)"],
  "AMAZON-IN & UAE":  ["Longest Side (>=1000px)", "Format (JPEG/TIFF/GIF)", "White Background (255,255,255)", "Color Mode (RGB)"],
  "AJIO":             ["Aspect Ratio (1:1.25)", "Dimensions (>1000x1000)", "Format (JPEG/PNG)", "Size (200KB-1MB)", "Color Mode (RGB)", "Resolution (72 DPI)"],
  "SHOPIFY":          ["Dimensions (>=1800x1440)", "Size (500KB-2MB)", "Format (JPG/JPEG)"],
  "NAMSHI":           ["Format (JPEG/JPG)", "Dimensions (>=660x900)", "Aspect Ratio (0.73)", "Resolution (>72 PPI)", "Color Mode (RGB)"],
  "DUBAI MALL":       ["Format (JPEG/JPG)", "Dimensions (>=660x900)", "Aspect Ratio (0.73)", "Resolution (>72 PPI)", "Color Mode (RGB)"],
};

// All unique conditions across all portals (in order of first appearance)
const MASTER_CONDITIONS = (() => {
  const seen = new Set();
  const list = [];
  Object.values(PORTAL_CONDITIONS).forEach(conds =>
    conds.forEach(c => { if (!seen.has(c)) { seen.add(c); list.push(c); } })
  );
  return list;
})();

const CHIP_COLORS = {
  "MYNTRA":           { bg: "#fce4ec", border: "#e91e63", text: "#880e4f" },
  "FLIPKART":         { bg: "#e3f2fd", border: "#1565c0", text: "#0d47a1" },
  "AMAZON-IN & UAE":  { bg: "#fff8e1", border: "#f57f17", text: "#e65100" },
  "AJIO":             { bg: "#e8f5e9", border: "#2e7d32", text: "#1b5e20" },
  "SHOPIFY":          { bg: "#f3e5f5", border: "#6a1b9a", text: "#4a148c" },
  "NAMSHI":           { bg: "#e0f7fa", border: "#00695c", text: "#004d40" },
  "DUBAI MALL":       { bg: "#fbe9e7", border: "#bf360c", text: "#b71c1c" },
};

const CUSTOM_COLOR = { bg: "#fffbe6", border: "#faad14", text: "#874d00" };

function loadCustomPortals() {
  try { return JSON.parse(localStorage.getItem("bm_custom_portals") || "{}"); }
  catch { return {}; }
}

function saveCustomPortals(cp) {
  try { localStorage.setItem("bm_custom_portals", JSON.stringify(cp)); } catch {}
}

export default class frmPortalImageValidator extends Component {
  constructor(props) {
    super(props);
    this.state = {
      uploadFile: null,
      folderFiles: [],
      selectedPortals: [],
      activeMode: "excel",
      loading: false,
      loadingMsg: "Processing, please wait...",

      // Output section toggles
      outputSections: {
        imageProperties: false,
        downloadImages: false,
        portalConditions: true,
      },

      // Custom portal management
      customPortals: loadCustomPortals(),
      managePortalsOpen: false,
      newPortalName: "",
      newPortalConditions: [],
    };
    this.pollInterval = null;
    this.folderInputRef = React.createRef();
  }

  componentWillUnmount() {
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
      this.pollInterval = null;
    }
    // Any poll fetch that is already in flight will complete after this; its
    // .then branches check `this._unmounted` and bail, so no Swal is fired
    // on a page the user has navigated away from.
    this._unmounted = true;
  }

  // ── Helpers ──────────────────────────────────────────────────────────────

  allPortals = () => [...PORTALS, ...Object.keys(this.state.customPortals)];

  conditionsFor = (portal) =>
    PORTAL_CONDITIONS[portal] || this.state.customPortals[portal] || [];

  chipColorFor = (portal) => CHIP_COLORS[portal] || CUSTOM_COLOR;

  // ── File handlers ─────────────────────────────────────────────────────────

  handleFileUpload = (e) => this.setState({ uploadFile: e.target.files[0] || null });

  handleFolderUpload = (e) => {
    const files = Array.from(e.target.files || []).filter(f => f.type.startsWith("image/"));
    this.setState({ folderFiles: files });
  };

  // ── Portal selection ──────────────────────────────────────────────────────

  togglePortal = (portal) => {
    this.setState(prev => {
      const set = new Set(prev.selectedPortals);
      if (set.has(portal)) set.delete(portal); else set.add(portal);
      return { selectedPortals: Array.from(set) };
    });
  };

  portalsParam = () => this.state.selectedPortals.join(",");

  // ── Custom portal management ──────────────────────────────────────────────

  toggleNewPortalCondition = (label) => {
    this.setState(prev => {
      const set = new Set(prev.newPortalConditions);
      if (set.has(label)) set.delete(label); else set.add(label);
      return { newPortalConditions: Array.from(set) };
    });
  };

  addCustomPortal = () => {
    const { newPortalName, newPortalConditions, customPortals } = this.state;
    const name = newPortalName.trim().toUpperCase();
    if (!name) { Swal.fire("Warning", "Enter a portal name", "warning"); return; }
    if (PORTALS.includes(name)) { Swal.fire("Warning", `"${name}" is a built-in portal and cannot be overridden`, "warning"); return; }
    if (newPortalConditions.length === 0) { Swal.fire("Warning", "Select at least one condition", "warning"); return; }
    const updated = { ...customPortals, [name]: [...newPortalConditions] };
    saveCustomPortals(updated);
    this.setState({ customPortals: updated, newPortalName: "", newPortalConditions: [] });
  };

  deleteCustomPortal = (name) => {
    this.setState(prev => {
      const cp = { ...prev.customPortals };
      delete cp[name];
      saveCustomPortals(cp);
      const sp = prev.selectedPortals.filter(p => p !== name);
      return { customPortals: cp, selectedPortals: sp };
    });
  };

  // ── Validate — Excel mode ─────────────────────────────────────────────────

  validateLinks = async () => {
    const { uploadFile, selectedPortals, outputSections, customPortals } = this.state;
    const token = localStorage.getItem("authToken1");

    if (!token) { Swal.fire("Unauthorized", "Please login again", "error"); return; }
    if (!uploadFile) { Swal.fire("Warning", "Please choose an Excel or CSV file first", "warning"); return; }

    try {
      this.setState({
        loading: true,
        loadingMsg: selectedPortals.length
          ? `Validating links against ${selectedPortals.join(", ")} conditions...`
          : "Validating image links...",
      });

      const formData = new FormData();
      formData.append("file", uploadFile);
      formData.append("concurrency", CONCURRENCY);

      if (outputSections.portalConditions && selectedPortals.length) {
        // Only send built-in portals here; custom portals go via custom_portals param
        const builtInSelected = selectedPortals.filter(p => PORTALS.includes(p));
        if (builtInSelected.length) formData.append("portals", builtInSelected.join(","));
      }

      formData.append("include_properties", outputSections.imageProperties ? "true" : "false");

      // Collect custom portal definitions for selected custom portals
      if (outputSections.portalConditions) {
        const relevantCustom = {};
        selectedPortals.forEach(p => { if (customPortals[p]) relevantCustom[p] = customPortals[p]; });
        if (Object.keys(relevantCustom).length > 0) {
          formData.append("custom_portals", JSON.stringify(relevantCustom));
        }
      }

      const response = await fetch(`${API_BASE}/validate-image-urls?db=${DB}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || data.message || "API error");

      const jobId = data.job_id;
      if (!jobId) throw new Error("Job ID missing in response");
      this.pollStatus(jobId, token);
    } catch (error) {
      console.error(error);
      this.setState({ loading: false });
      Swal.fire("Error", error.message, "error");
    }
  };

  pollStatus = (jobId, token) => {
    if (this._unmounted) return;
    // Starting a fresh poll loop supersedes any previous one. Overwriting
    // this.pollInterval without clearing the old handle would leak that
    // interval -- both would then fire against the same job id and both could
    // raise a dialog, which is one of the ways the error popup can appear
    // where it is not wanted.
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
      this.pollInterval = null;
    }

    // Count consecutive polling failures and give up after too many.
    // The previous version swallowed every polling error forever, so a dead
    // backend left the overlay up until the user reloaded the tab.
    let networkFails = 0;
    const MAX_FAILS = 20;   // ~1 minute at 3s intervals

    const stop = () => {
      clearInterval(this.pollInterval);
      this.pollInterval = null;
      this.setState({ loading: false });
    };

    const esc = (t) =>
      String(t == null ? "" : t).replace(/[&<>]/g, (c) =>
        ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" })[c]);

    // Show whatever the backend actually said instead of "Validation failed".
    // A pydantic dump, an HTTP string, a stack trace -- all more useful than
    // the generic placeholder. Falls back to the generic only when nothing
    // usable was returned.
    const showServerError = (data) => {
      const msg = (data && (data.error || data.message)) || "";
      const trace = data && data.traceback ? String(data.traceback) : "";
      const html =
        "<div style='text-align:left'>" +
        (msg
          ? "<div style='margin-bottom:8px'>" + esc(msg) + "</div>"
          : "<div style='margin-bottom:8px;color:#666'>The validator reported a failure but did not say why.</div>") +
        (trace
          ? "<details style='margin-top:6px'>" +
            "<summary style='cursor:pointer;color:#888;font-size:12px'>Server trace</summary>" +
            "<pre style='text-align:left;max-height:220px;overflow:auto;font-size:11px;" +
            "background:#f6f6f6;padding:8px;border-radius:4px;white-space:pre-wrap'>" +
            esc(trace) + "</pre></details>"
          : "") +
        "</div>";
      Swal.fire({ icon: "error", title: "Validation failed", html, width: 640 });
    };

    this.pollInterval = setInterval(async () => {
      let response, data;
      try {
        response = await fetch(`${API_BASE}/job-status/${jobId}?db=${DB}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        data = await response.json();
        networkFails = 0;    // any readable reply counts as the backend being up
      } catch (err) {
        networkFails += 1;
        console.log("Polling error (" + networkFails + "/" + MAX_FAILS + "):", err.message);
        if (networkFails >= MAX_FAILS) {
          stop();
          if (this._unmounted) return;
          Swal.fire({
            icon: "error",
            title: "Lost contact with the server",
            html:
              "<div style='text-align:left'>" +
              "The job may still be running on the server, but this page cannot see its status any more. " +
              "<br/><br/>" +
              "Reload the page in a moment and the validator will pick up where it left off, or restart the push " +
              "if nothing comes through.<br/><br/>" +
              "<span style='color:#888;font-size:12px;font-family:monospace'>last error: " +
              esc(err.message) + "</span></div>",
            width: 560,
          });
        }
        return;
      }

      const state = (data.state || "").toUpperCase();

      if (state === "SUCCESS" || state === "COMPLETED") {
        stop();
        if (this._unmounted) return;
        this.showDownloadChoice(jobId, token, !!data.zip_file);
        return;
      }
      if (state === "FAILURE" || state === "FAILED" || state === "ERROR") {
        stop();
        if (this._unmounted) return;
        showServerError(data);
        return;
      }
      // PENDING / PROGRESS / STARTED — keep polling, nothing to show.
    }, POLL_INTERVAL_MS);
  };

  showDownloadChoice = async (jobId, token, hasZip) => {
    const result = await Swal.fire({
      title: "Validation Complete",
      html: "<p style='color:#555;font-size:14px'>Choose which data to download:</p>",
      showCancelButton: true,
      showDenyButton: true,
      confirmButtonText: "✓ Complete Data",
      denyButtonText: "✗ Incomplete Data",
      cancelButtonText: "⬇ Download All",
      confirmButtonColor: "#00a65a",
      denyButtonColor: "#dd4b39",
      cancelButtonColor: "#3490dc",
      allowOutsideClick: false,
    });

    let filter = "all";
    if (result.isConfirmed) filter = "complete";
    else if (result.isDenied) filter = "incomplete";
    // isDismissed with reason='cancel' → "all"

    await this.downloadResult(jobId, token, hasZip, filter);
  };

  triggerDownload = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  downloadResult = async (jobId, token, hasZip, filter = "all") => {
    const { outputSections } = this.state;
    try {
      const endpoint = filter === "all"
        ? `${API_BASE}/download/${jobId}?db=${DB}`
        : `${API_BASE}/download-filtered/${jobId}?db=${DB}&filter=${filter}`;
      const r = await fetch(endpoint, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error("Excel download failed");
      const fname = filter === "all" ? "PortalValidationResult.xlsx" : `PortalValidation_${filter}.xlsx`;
      this.triggerDownload(await r.blob(), fname);
    } catch (err) {
      Swal.fire("Error", err.message || "Excel download failed", "error");
      return;
    }

    if (hasZip && outputSections.downloadImages) {
      try {
        const zr = await fetch(`${API_BASE}/download-images/${jobId}?db=${DB}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (zr.ok) this.triggerDownload(await zr.blob(), "ValidImages.zip");
      } catch (e) { console.warn("ZIP download failed:", e.message); }
    }

    Swal.fire({ icon: "success", title: "Download Complete", timer: 2000, showConfirmButton: false });
  };

  // ── Validate — Folder mode ────────────────────────────────────────────────

  validateFolder = async () => {
    const { folderFiles, selectedPortals, customPortals } = this.state;
    const token = localStorage.getItem("authToken1");

    if (!token) { Swal.fire("Unauthorized", "Please login again", "error"); return; }
    if (!folderFiles.length) { Swal.fire("Warning", "Please choose a folder with images first", "warning"); return; }

    // Ask Complete / Incomplete / All (same pattern as Excel links)
    const result = await Swal.fire({
      title: "Validation Complete",
      html: `<p style='color:#555;font-size:14px'>Choose which data to download:</p>${
        selectedPortals.length
          ? `<div style="font-size:12px;color:#888">Portal(s): ${selectedPortals.join(", ")} · ${folderFiles.length} image(s)</div>`
          : `<div style="font-size:12px;color:#888">${folderFiles.length} image(s)</div>`
      }`,
      showCancelButton: true,
      showDenyButton: true,
      confirmButtonText: "✓ Complete Data",
      denyButtonText: "✗ Incomplete Data",
      cancelButtonText: "⬇ Download All",
      confirmButtonColor: "#00a65a",
      denyButtonColor: "#dd4b39",
      cancelButtonColor: "#3490dc",
      allowOutsideClick: false,
    });

    if (result.isDismissed && result.dismiss !== Swal.DismissReason.cancel) {
      return; // ESC key — abort
    }

    let filter = "all";
    if (result.isConfirmed) filter = "complete";
    else if (result.isDenied) filter = "incomplete";
    // isDismissed with cancel reason → "all" (Download All button)

    try {
      this.setState({
        loading: true,
        loadingMsg: `Checking ${folderFiles.length} image(s)${selectedPortals.length ? ` against ${selectedPortals.join(", ")}` : ""}...`,
      });

      const formData = new FormData();
      const builtInSelected = selectedPortals.filter(p => PORTALS.includes(p));
      const relevantCustom = {};
      selectedPortals.forEach(p => { if (customPortals[p]) relevantCustom[p] = customPortals[p]; });
      if (builtInSelected.length) formData.append("portals", builtInSelected.join(","));
      if (Object.keys(relevantCustom).length > 0) formData.append("custom_portals", JSON.stringify(relevantCustom));
      folderFiles.forEach(f => formData.append("images[]", f));

      const response = await fetch(`${API_BASE}/validate-portal-folder?db=${DB}&filter=${filter}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || "Folder validation failed");
      }

      const tag = selectedPortals.length ? selectedPortals.join("_") : "AllImages";
      this.triggerDownload(await response.blob(), `${tag}_FolderValidation_${filter}.xlsx`);
      this.setState({ loading: false });
      Swal.fire({ icon: "success", title: "Validation Complete", text: `Results for ${folderFiles.length} image(s) downloaded.` });
    } catch (error) {
      console.error(error);
      this.setState({ loading: false });
      Swal.fire("Error", error.message, "error");
    }
  };

  // ── Render helpers ────────────────────────────────────────────────────────

  renderPortalSelector() {
    const { selectedPortals, loading, customPortals } = this.state;
    const allPortals = this.allPortals();
    const hasPortals = selectedPortals.length > 0;

    return (
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10, flexWrap: "wrap" }}>
          {/* Left: portal controls */}
          <label style={{ margin: 0, fontWeight: 600 }}>Select Portal(s) *</label>
          <button className="btn btn-xs btn-default" onClick={() => this.setState({ selectedPortals: [...allPortals] })} disabled={loading}>Select All</button>
          <button className="btn btn-xs btn-default" onClick={() => this.setState({ selectedPortals: [] })} disabled={loading}>Clear</button>
          <button
            className="btn btn-xs btn-default"
            onClick={() => this.setState(prev => ({ managePortalsOpen: !prev.managePortalsOpen }))}
            style={{ fontSize: 12 }}
          >
            {this.state.managePortalsOpen ? "▲ Manage Portals" : "▼ Manage Portals"}
          </button>
          {hasPortals && <span style={{ fontSize: 12, color: "#666" }}>{selectedPortals.length} selected</span>}

          {/* Right: output section toggles inline */}
          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: "#1890ff", marginRight: 2 }}>Output:</span>
            {[
              { key: "urlStatus",        label: "Image URL Status",  always: true },
              { key: "imageProperties",  label: "Image Properties",  always: false },
              { key: "downloadImages",   label: "Download Images",   always: false },
              { key: "portalConditions", label: "Portal Conditions", always: false },
            ].map(({ key, label, always }) => {
              const checked = always || this.state.outputSections[key];
              return (
                <label key={key} title={always ? "Always included" : `Toggle ${label}`} style={{
                  display: "flex", alignItems: "center", gap: 4,
                  padding: "3px 10px",
                  border: `1px solid ${checked ? "#1890ff" : "#d9d9d9"}`,
                  borderRadius: 20,
                  background: checked ? "#e6f7ff" : "#f9f9f9",
                  color: checked ? "#0050b3" : "#777",
                  fontSize: 11, fontWeight: checked ? 600 : 400,
                  cursor: always ? "default" : "pointer",
                  userSelect: "none",
                }}>
                  <input type="checkbox" checked={checked} disabled={always}
                    onChange={() => !always && this.setState(prev => ({
                      outputSections: { ...prev.outputSections, [key]: !prev.outputSections[key] }
                    }))}
                    style={{ display: "none" }}
                  />
                  {checked ? "✓" : "○"} {label}
                  {always && <span style={{ fontSize: 9, opacity: 0.55 }}>(always)</span>}
                </label>
              );
            })}
          </div>
        </div>

        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14 }}>
          {allPortals.map(portal => {
            const checked = selectedPortals.includes(portal);
            const color = this.chipColorFor(portal);
            const isCustom = !!customPortals[portal];
            return (
              <label key={portal} style={{
                display: "flex", alignItems: "center", gap: 5,
                padding: "6px 14px",
                border: `2px solid ${checked ? color.border : "#ccc"}`,
                borderRadius: 20,
                background: checked ? color.bg : "#f9f9f9",
                color: checked ? color.text : "#555",
                cursor: loading ? "not-allowed" : "pointer",
                fontWeight: checked ? 700 : 400,
                fontSize: 13,
                transition: "all 0.15s",
                userSelect: "none",
                position: "relative",
              }}>
                <input type="checkbox" checked={checked} onChange={() => this.togglePortal(portal)} disabled={loading} style={{ display: "none" }} />
                {checked && <span style={{ fontSize: 11, fontWeight: 900 }}>✓</span>}
                {portal}
                {isCustom && <span style={{ fontSize: 9, marginLeft: 2, opacity: 0.7 }}>★</span>}
              </label>
            );
          })}
        </div>

        {hasPortals && (
          <div style={{ background: "#f8f8f8", border: "1px solid #e0e0e0", borderRadius: 6, padding: "10px 14px", marginBottom: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: "#555", marginBottom: 8 }}>Conditions that will be checked:</div>
            {selectedPortals.map(portal => {
              const color = this.chipColorFor(portal);
              return (
                <div key={portal} style={{ marginBottom: 8, display: "flex", flexWrap: "wrap", alignItems: "center", gap: 5 }}>
                  <span style={{ padding: "2px 10px", background: color.bg, border: `1px solid ${color.border}`, borderRadius: 4, color: color.text, fontSize: 12, fontWeight: 700, whiteSpace: "nowrap" }}>
                    {portal}
                  </span>
                  {this.conditionsFor(portal).map(c => (
                    <span key={c} style={{ padding: "2px 7px", background: "#fff", border: "1px solid #ddd", borderRadius: 4, fontSize: 11, color: "#444" }}>{c}</span>
                  ))}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  renderOutputSections() {
    const { outputSections } = this.state;
    const sections = [
      { key: "urlStatus",        label: "Image URL Status",      desc: "Check if each URL is reachable and returns an image", always: true },
      { key: "imageProperties",  label: "Image Properties",      desc: "Include dimensions, size, format, DPI in output",     always: false },
      { key: "downloadImages",   label: "Download Images",       desc: "Download valid images as ZIP after validation",       always: false },
      { key: "portalConditions", label: "Portal Conditions",     desc: "Check portal-specific rules (size, aspect ratio…)",   always: false },
    ];

    return (
      <div style={{ background: "#f0f7ff", border: "1px solid #bfd9ff", borderRadius: 6, padding: "10px 14px", marginBottom: 14 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: "#1890ff", marginBottom: 8 }}>Output Sections</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {sections.map(({ key, label, desc, always }) => {
            const checked = always || outputSections[key];
            return (
              <label key={key} title={desc} style={{
                display: "flex", alignItems: "center", gap: 6,
                padding: "6px 12px",
                border: `2px solid ${checked ? "#1890ff" : "#d9d9d9"}`,
                borderRadius: 20, cursor: always ? "default" : "pointer",
                background: checked ? "#e6f7ff" : "#fff",
                color: checked ? "#0050b3" : "#555",
                fontSize: 12, fontWeight: checked ? 600 : 400,
                userSelect: "none", transition: "all 0.15s",
                opacity: always ? 0.85 : 1,
              }}>
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={always}
                  onChange={() => !always && this.setState(prev => ({
                    outputSections: { ...prev.outputSections, [key]: !prev.outputSections[key] }
                  }))}
                  style={{ display: "none" }}
                />
                <span style={{ fontSize: 11, fontWeight: 900 }}>{checked ? "✓" : "○"}</span>
                {label}
                {always && <span style={{ fontSize: 10, opacity: 0.6, marginLeft: 2 }}>(always on)</span>}
              </label>
            );
          })}
        </div>
      </div>
    );
  }

  renderManagePortals() {
    const { managePortalsOpen, customPortals, newPortalName, newPortalConditions } = this.state;

    return (
      <div style={{ marginBottom: 14 }}>
        {managePortalsOpen && (
          <div style={{ border: "1px solid #d6e4ff", borderRadius: 6, padding: 14, background: "#f6f9ff", marginTop: 8 }}>

            {/* Built-in portals (read-only) */}
            <div style={{ marginBottom: 12 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: "#555", marginRight: 8 }}>Built-in portals:</span>
              {PORTALS.map(p => (
                <span key={p} style={{ display: "inline-block", padding: "2px 8px", background: "#e6f4ea", border: "1px solid #52c41a", borderRadius: 4, fontSize: 11, color: "#237804", marginRight: 4, marginBottom: 4 }}>{p}</span>
              ))}
            </div>

            {/* Custom portals */}
            {Object.keys(customPortals).length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: "#555", marginRight: 8 }}>Custom portals (★):</span>
                {Object.entries(customPortals).map(([p, conds]) => (
                  <span key={p} style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "2px 8px", background: "#fffbe6", border: "1px solid #faad14", borderRadius: 4, fontSize: 11, color: "#874d00", marginRight: 6, marginBottom: 4 }}>
                    <span title={conds.join(", ")}>{p}</span>
                    <span style={{ opacity: 0.6, fontSize: 9 }}>{conds.length} checks</span>
                    <button onClick={() => this.deleteCustomPortal(p)} style={{ border: "none", background: "none", cursor: "pointer", color: "#ff4d4f", fontSize: 13, padding: 0, lineHeight: 1 }}>✕</button>
                  </span>
                ))}
              </div>
            )}

            {/* Add portal form */}
            <div style={{ borderTop: "1px solid #dde", paddingTop: 12, marginTop: 4 }}>
              <div style={{ fontWeight: 600, fontSize: 13, color: "#333", marginBottom: 8 }}>Add New Portal</div>
              <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10 }}>
                <input
                  type="text"
                  placeholder="Portal name (e.g. TRENDYOL)"
                  value={newPortalName}
                  onChange={e => this.setState({ newPortalName: e.target.value.toUpperCase() })}
                  style={{ padding: "4px 8px", border: "1px solid #d9d9d9", borderRadius: 4, fontSize: 13, width: 240 }}
                />
              </div>
              <div style={{ fontSize: 12, color: "#666", marginBottom: 6 }}>
                Select conditions <span style={{ color: "#888" }}>(existing checks only — {MASTER_CONDITIONS.length} available)</span>:
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, maxHeight: 180, overflowY: "auto", padding: "6px 0" }}>
                {MASTER_CONDITIONS.map(label => {
                  const checked = newPortalConditions.includes(label);
                  return (
                    <label key={label} style={{
                      display: "flex", alignItems: "center", gap: 5,
                      padding: "3px 10px",
                      border: `1px solid ${checked ? "#1890ff" : "#d9d9d9"}`,
                      borderRadius: 4,
                      background: checked ? "#e6f7ff" : "#fff",
                      color: checked ? "#0050b3" : "#444",
                      fontSize: 11, cursor: "pointer", userSelect: "none",
                    }}>
                      <input type="checkbox" checked={checked} onChange={() => this.toggleNewPortalCondition(label)} style={{ margin: 0 }} />
                      {label}
                    </label>
                  );
                })}
              </div>
              {newPortalConditions.length > 0 && (
                <div style={{ fontSize: 11, color: "#1890ff", marginTop: 6 }}>{newPortalConditions.length} condition(s) selected</div>
              )}
              <button
                className="btn btn-sm btn-primary"
                style={{ marginTop: 10 }}
                onClick={this.addCustomPortal}
                disabled={!newPortalName.trim() || newPortalConditions.length === 0}
              >
                + Add Portal
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── Main render ───────────────────────────────────────────────────────────

  render() {
    const { loading, loadingMsg, selectedPortals, activeMode, folderFiles, uploadFile, outputSections } = this.state;
    const hasPortals = selectedPortals.length > 0;

    return (
      <div className="content-wrapper">
        {loading && (
          <div className="loader">
            <div className="loader-item" style={{ marginLeft: "75vh" }}>
              <Spin tip={loadingMsg} />
            </div>
          </div>
        )}

        <section className="content">
          <div className="box box-success" style={{ marginTop: "-13px" }}>
            <div className="box-header with-border" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '12px 20px' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 11, color: '#aaa', marginBottom: 3 }}>
                  Dashboard &gt; Image Tools &gt; Portal Image Validator
                </div>
                <h3 className="box-title" style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>
                  Portal Image Validator
                </h3>
              </div>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 4, flexWrap: 'wrap' }}>
              </div>
            </div>

            <div className="box-body">

              {/* Portal selector */}
              <div className="row">
                <div className="col-xs-12">
                  {this.renderPortalSelector()}
                </div>
              </div>

              {/* Manage portals */}
              <div className="row">
                <div className="col-xs-12">
                  {this.renderManagePortals()}
                </div>
              </div>

              {/* Mode tabs */}
              <div className="row">
                <div className="col-xs-12">
                  <ul className="nav nav-tabs" style={{ borderBottom: "2px solid #00a65a" }}>
                    {[
                      { key: "excel",  label: "Validate Excel Links" },
                      { key: "folder", label: "Validate Image Folder" },
                    ].map(({ key, label }) => (
                      <li key={key} className={activeMode === key ? "active" : ""}>
                        <a href="#" onClick={e => { e.preventDefault(); this.setState({ activeMode: key }); }}
                          style={{ fontWeight: activeMode === key ? "bold" : "normal", color: activeMode === key ? "#00a65a" : "#555" }}>
                          {label}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Excel mode */}
              {activeMode === "excel" && (
                <div className="row" style={{ marginTop: 16 }}>
                  <div className="col-xs-12 col-sm-6 col-md-6 margintop">
                    <label>Upload Excel File *</label>
                    <input type="file" accept=".xlsx,.xlsm,.csv" className="form-control" onChange={this.handleFileUpload} />
                    {uploadFile && <span style={{ fontSize: 12, color: "#555", marginTop: 4, display: "block" }}>{uploadFile.name}</span>}
                  </div>

                  <div className="col-xs-12" style={{ marginTop: 24, textAlign: "center" }}>
                    <button className="btn btn-primary btn-lg" onClick={this.validateLinks} disabled={loading}>
                      VALIDATE LINKS
                      {hasPortals && ` — ${selectedPortals.length} PORTAL${selectedPortals.length > 1 ? "S" : ""}`}
                    </button>
                    {hasPortals && (
                      <p style={{ marginTop: 8, fontSize: 12, color: "#777" }}>
                        Output sections: URL Status
                        {outputSections.imageProperties && " + Image Properties"}
                        {outputSections.portalConditions && " + Portal Conditions"}
                        {outputSections.downloadImages && " + Images ZIP"}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Folder mode */}
              {activeMode === "folder" && (
                <div className="row" style={{ marginTop: 16 }}>
                  <div className="col-xs-12 col-sm-6 col-md-6 margintop">
                    <label>Select Image Folder *</label>
                    <input
                      type="file" accept="image/*" multiple
                      webkitdirectory="true" directory="true"
                      className="form-control"
                      ref={this.folderInputRef}
                      onChange={this.handleFolderUpload}
                    />
                    {folderFiles.length > 0 && (
                      <span style={{ fontSize: 12, color: "#555", marginTop: 4, display: "block" }}>{folderFiles.length} image file(s) selected</span>
                    )}
                  </div>

                  <div className="col-xs-12" style={{ marginTop: 24, textAlign: "center" }}>
                    <button className="btn btn-success btn-lg" onClick={this.validateFolder} disabled={loading}>
                      VALIDATE FOLDER
                      {hasPortals && ` — ${selectedPortals.length} PORTAL${selectedPortals.length > 1 ? "S" : ""}`}
                    </button>
                    <p style={{ marginTop: 8, fontSize: 12, color: "#777" }}>Output Excel: one row per image with Overall status + individual condition columns per portal.</p>
                  </div>
                </div>
              )}

            </div>
          </div>
        </section>
      </div>
    );
  }
}
